import { Collapse, DatePicker, Form, Input, Select, Space, Typography } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { AgentSelect } from '@/features/agent/AgentSelect'
import { SubscriberSelect } from '@/features/subscriber/SubscriberSelect'
import { useAuth } from '@/shared/auth/useAuth'
import { getCurrencyCode } from '@/shared/lib/format.money'
import { FilterDrawer } from '@/shared/ui/FilterDrawer'
import type { TableState } from '@/shared/ui/table/tableState'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import {
  BYTE_UNIT,
  LEDGER_ACCOUNT_TYPES,
  LEDGER_OWNER_TYPES,
  looksLikeOwnerId,
  ownerPickerKind,
} from './ledgerApi'
import {
  JOURNAL_TXN_KINDS,
  journalActiveFilterCount,
  journalDayRange,
} from './ledgerJournalApi'

type Filters = TableState['filters']

const DAY_FORMAT = 'YYYY-MM-DD'

function single(values: readonly string[] | undefined): string | undefined {
  return values && values.length > 0 ? values[0] : undefined
}

/**
 * Tranzaksiyalar ro'yxatining filtrlari.
 *
 * <h2>⚠️ DAVR TASHQARIDA, QOLGANI PANELDA (egasi qarori 2026-08-20)</h2>
 * Sana oralig'i — bu ro'yxatning ENG ko'p ishlatiladigan boshqaruvi
 * (kassir kun bo'yi "bugun" va "kecha" orasida yuradi) va uni har safar
 * panel ochib o'zgartirish har amalga ikki bosish qo'shardi. Qolgan
 * beshtasi bir qatorga sig'masdi — ular o'ngdan ochiladigan panelda.
 *
 * <h2>⚠️ HOLAT SHU YERDA SAQLANMAYDI</h2>
 * Manba haqiqat — MANZIL (`useTableUrlState`), aynan `DataTable`
 * ishlatadigan hookning o'zi. O'z `useState` i ikkinchi haqiqat manbai
 * bo'lardi va ular birinchi qayta chizishdayoq ajralardi.
 */
export function JournalFilterBar() {
  const { t } = useTranslation()
  const { state, patch } = useTableUrlState()
  const { user } = useAuth()
  const currency = user?.tenant.currency ?? getCurrencyCode()

  const range = journalDayRange(state.filters)

  /**
   * ⚠️ Filtr o'zgarganda sahifa BIRINCHIGA qaytariladi: 5-sahifada turib
   * filtr qo'yilsa va natija ikki sahifa qolsa, ekran BO'SH chiqardi va
   * operator uni "hech narsa topilmadi" deb o'qirdi.
   */
  const commit = (next: Filters) => patch({ page: 1, filters: next })

  const commitRange = (value: [Dayjs, Dayjs]) => {
    commit({
      ...state.filters,
      from: [value[0].format(DAY_FORMAT)],
      to: [value[1].format(DAY_FORMAT)],
    })
  }

  return (
    <Space wrap size="small">
      {/*
        ⚠️ `allowClear={false}` — davr HAR DOIM bor. Serverda ham chegarasiz
        so'rov yo'q (`JournalQuery` `null` ni qabul qilmaydi), ya'ni bo'sh
        tanlagich "hammasi" degan yolg'on va'da bo'lardi.
      */}
      <DatePicker.RangePicker
        value={[dayjs(range.from), dayjs(range.to)]}
        onChange={(value) => {
          if (value?.[0] && value[1]) commitRange([value[0], value[1]])
        }}
        allowClear={false}
        format="DD.MM.YYYY"
      />

      <FilterDrawer<Filters>
        value={state.filters}
        /* «Tozalash» faqat PANELDAGI maydonlarni bo'shatadi — davr
           operatorning ko'z oldida turibdi va uni panel ichidan jimgina
           qaytarish kutilmagan bo'lardi */
        resetValue={{ ...state.filters, kind: [], accountType: [], ownerType: [], ownerId: [], unit: [] }}
        activeCount={journalActiveFilterCount(state.filters)}
        onApply={commit}
        title={t('ledgerJournal.filters.title')}
      >
        {(draft, patchDraft) => (
          <JournalFilterFields
            draft={draft}
            currency={currency}
            setFilter={(key, value) => patchDraft({ [key]: value ? [value] : [] })}
          />
        )}
      </FilterDrawer>
    </Space>
  )
}

/** Panel ichidagi maydonlar — hammasi QORALAMA ustida, so'rov yuborilmaydi */
function JournalFilterFields({
  draft,
  currency,
  setFilter,
}: {
  draft: Filters
  currency: string
  setFilter: (key: string, value: string | undefined) => void
}) {
  const { t } = useTranslation()
  const ownerId = single(draft['ownerId']) ?? ''
  const ownerIdRejected = ownerId !== '' && !looksLikeOwnerId(ownerId)
  const ownerType = single(draft['ownerType'])
  const ownerSelect = ownerPickerKind(ownerType)

  /*
    ⚠️ MAYDON BITTA NUSXADA yasaladi va IKKI joyda ishlatiladi: tur
    tanlanmaganda ochiq, tanlanganda yig'iladigan bo'lim ichida. JSX
    ni ikki marta yozish ikkinchi nusxa bo'lardi va ular birinchi
    o'zgarishdayoq ajrashib ketardi — masalan ogohlantirish faqat
    bittasida qolardi.
  */
  const ownerIdField = (
    <Form.Item
      label={t('ledgerJournal.filters.ownerId')}
      {...(ownerIdRejected
        ? { validateStatus: 'warning' as const, extra: t('ledgerJournal.filters.ownerIdHint') }
        : /*
            ⚠️ TUR TANLANMAGANDA IZOH KO'RSATILADI.

            Tur tanlanmagan bo'lsa panel QAYSI reyestrdan qidirishni
            bilmaydi: bitta UUID abonentniki ham, agentniki ham, PSP
            niki ham bo'lishi mumkin va ular BOG'LANMAGAN
            (`OwnerType` javadoc'i). Bittasini taxmin qilib tanlagich
            ochish ikkinchisini jimgina yashirardi. Shuning uchun bu
            holatda qo'lda kiritishning O'ZI qoladi — lekin operator
            tanlagichni QANDAY yoqishini bilib tursin.
          */
          ownerSelect === null
          ? { extra: t('ledgerJournal.filters.ownerIdPickHint') }
          : {})}
    >
      <Input
        allowClear
        value={ownerId}
        onChange={(event) => setFilter('ownerId', event.target.value.trim())}
        placeholder={t('ledgerJournal.filters.ownerIdPlaceholder')}
      />
    </Form.Item>
  )

  return (
    <>
      <Form.Item label={t('ledgerJournal.filters.kind')}>
        <Select
          allowClear
          style={{ width: '100%' }}
          placeholder={t('ledgerJournal.filters.kind')}
          value={single(draft['kind'])}
          onChange={(value?: string) => setFilter('kind', value)}
          options={JOURNAL_TXN_KINDS.map((kind) => ({
            value: kind,
            label: t(`txnKind.${kind}`, { defaultValue: kind }),
          }))}
        />
      </Form.Item>

      <Form.Item label={t('ledgerJournal.filters.accountType')}>
        <Select
          allowClear
          style={{ width: '100%' }}
          placeholder={t('ledgerJournal.filters.accountType')}
          value={single(draft['accountType'])}
          onChange={(value?: string) => setFilter('accountType', value)}
          options={LEDGER_ACCOUNT_TYPES.map((type) => ({
            value: type,
            label: t(`ledgerAccountType.${type}`, { defaultValue: type }),
          }))}
        />
      </Form.Item>

      <Form.Item label={t('ledgerJournal.filters.ownerType')}>
        <Select
          allowClear
          style={{ width: '100%' }}
          placeholder={t('ledgerJournal.filters.ownerType')}
          value={ownerType}
          /*
            ⚠️ TUR O'ZGARSA IDENTIFIKATOR TOZALANADI.

            Abonentning UUID'i `ownerType=AGENT` ostida hech qanday
            yozuvga mos kelmaydi — server ikkala ustunni ham
            solishtiradi. Eski qiymat qolib ketsa ro'yxat BO'SH chiqardi
            va sababi ekranda ko'rinmasdi: ikkala maydon ham to'ldirilgan
            bo'lib turardi. (`RegionDistrictIdFields` dagi viloyat->tuman
            tozalash qarori bilan bir xil.)
          */
          onChange={(value?: string) => {
            setFilter('ownerType', value)
            setFilter('ownerId', undefined)
          }}
          options={LEDGER_OWNER_TYPES.map((type) => ({
            value: type,
            label: t(`ledgerOwnerType.${type}`, { defaultValue: type }),
          }))}
        />
      </Form.Item>

      {/*
        ⚠️ TANLAGICH ASOSIY YO'L, UUID ESA QOLADI.

        Ilgari bu yerda faqat `Input` turardi — ya'ni maydon odamdan
        UUID TERISHNI so'rardi, holbuki operator uni hech qayerdan
        ko'rmaydi: ro'yxatda ham, kartochkada ham login va nom turadi.

        Qo'lda kiritish OLIB TASHLANMADI va bu ataylab: havola bilan
        kelgan qiymat, qo'llab-quvvatlash so'rovi va qo'lda tekshirish
        shunga tayanadi. Ikkala boshqaruv BITTA qiymatni yozadi, ya'ni
        tanlov tanlagichda ham, kiritgichda ham bir vaqtda ko'rinadi.
      */}
      {ownerSelect === 'AGENT' ? (
        <Form.Item label={t('ledgerJournal.filters.ownerAgent')}>
          {/*
            ⚠️ `kinds` BERILMAYDI. Jurnalda reseller ham `AGENT` turi
            ostida yotadi (`agentLinks` da o'lchangan: `AccountRef`
            `OwnerType.AGENT` ni QOTIRIB yozadi), ya'ni ro'yxatni turga
            qarab kesish kerakli tugunni JIMGINA yashirardi.
          */}
          <AgentSelect
            value={ownerId === '' ? null : ownerId}
            onChange={(value) => setFilter('ownerId', value ?? undefined)}
            placeholder={t('ledgerJournal.filters.ownerAgentPlaceholder')}
          />
        </Form.Item>
      ) : null}

      {ownerSelect === 'SUBSCRIBER' ? (
        <Form.Item label={t('ledgerJournal.filters.ownerSubscriber')}>
          <SubscriberSelect
            value={ownerId === '' ? null : ownerId}
            onChange={(value) => setFilter('ownerId', value ?? undefined)}
            placeholder={t('ledgerJournal.filters.ownerSubscriberPlaceholder')}
          />
        </Form.Item>
      ) : null}

      {/*
        ⚠️ Ega identifikatori UUID va u SERVER TIPIDA tekshiriladi
        (`@RequestParam UUID ownerId` — yaroqsiz satr 400). Panel bunday
        qiymatni yubormaydi va sababini AYNAN shu yerda aytadi: aks holda
        operator "qidiruv ishlamayapti" degan xulosaga kelardi.
      */}
      {ownerSelect === null ? (
        ownerIdField
      ) : (
        /*
          ⚠️ TANLAGICH OCHILGANDA UUID YIG'ILADI — OLIB TASHLANMAYDI.

          Ilgari ikkalasi BIRGA turardi: tanlagich, va uning ostida
          yana «Ega identifikatori (UUID)». Ekranda bu ikki xil filtr
          bo'lib ko'rinardi, holbuki ular BITTA qiymatni yozadi — va
          operator qaysi biri "asosiy" ekanini bilmasdi.

          ⚠️ Kiritgichning O'ZI qoladi va bu ataylab: havola bilan
          kelgan qiymat, qo'llab-quvvatlash so'rovi va qo'lda tekshirish
          shunga tayanadi (yuqoridagi izoh). Faqat u endi KO'ZGA
          TASHLANMAYDI — yig'ilgan bo'limda.

          ⚠️ YAROQSIZ QIYMATDA BO'LIM OCHIQ ochiladi. Aks holda
          ogohlantirish ("bu UUID emas, server 400 beradi") yopiq
          bo'lim ichida qolardi va operator bo'sh ro'yxatning sababini
          KO'RMASDI — ya'ni tuzatilayotgan nuqsonning aynan o'zi,
          faqat teskari tomondan.
        */
        <Collapse
          size="small"
          ghost
          style={{ marginBottom: 16 }}
          defaultActiveKey={ownerIdRejected ? ['manual'] : []}
          items={[
            {
              key: 'manual',
              label: t('ledgerJournal.filters.ownerIdManual'),
              children: ownerIdField,
            },
          ]}
        />
      )}

      {/*
        ⚠️ BIRLIK — jurnalda pul va trafik YONMA-YON yashaydi va
        yozuvlarning ko'pchiligi trafik (`JournalQuery` javadoc'i). Ro'yxat
        ikkitagina: tenantning valyutasi va `BYTE`. Uchinchisini o'ylab
        topish "boshqa birlik yo'q" degan yolg'on bo'lardi, shuning uchun
        maydon `showSearch` bilan — kerak bo'lsa kod qo'lda yoziladi.
      */}
      <Form.Item label={t('ledgerJournal.filters.unit')}>
        <Select
          allowClear
          showSearch
          style={{ width: '100%' }}
          placeholder={t('ledgerJournal.filters.unit')}
          value={single(draft['unit'])}
          onChange={(value?: string) => setFilter('unit', value)}
          options={[
            { value: currency, label: t('ledgerJournal.filters.unitMoney', { code: currency }) },
            { value: BYTE_UNIT, label: t('ledgerJournal.filters.unitTraffic') },
          ]}
        />
      </Form.Item>

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t('ledgerJournal.filters.hint')}
      </Typography.Text>
    </>
  )
}
